(function (root, factory) {
  const model = typeof module === 'object' && module.exports
    ? require('./dim-text-model.js')
    : root.DIMTextModel;
  const api = factory(model);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMTextRenderer = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, (model) => {
  'use strict';

  const arcPoint = (distance, curvature) => {
    if (Math.abs(curvature) < 1e-8) return { x: distance, y: 0, angle: 0 };
    const angle = curvature * distance;
    return {
      x: Math.sin(angle) / curvature,
      y: 2 * Math.sin(angle / 2) ** 2 / curvature,
      angle
    };
  };

  const configure = (context, font, size) => {
    context.font = `${font.weight} ${size}px ${font.family}`;
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
    context.direction = 'ltr';
    context.fontKerning = 'none';
    if ('letterSpacing' in context) context.letterSpacing = '0px';
    if ('wordSpacing' in context) context.wordSpacing = '0px';
  };

  const glyphMetrics = (context, text) => {
    const measured = context.measureText(text);
    const fields = [
      'width', 'actualBoundingBoxLeft', 'actualBoundingBoxRight',
      'actualBoundingBoxAscent', 'actualBoundingBoxDescent'
    ];
    if (!measured || fields.some((field) => !Number.isFinite(measured[field]))) {
      throw new Error('Your browser cannot measure this lettering accurately.');
    }
    return Object.freeze({
      width: measured.width,
      left: measured.actualBoundingBoxLeft,
      right: measured.actualBoundingBoxRight,
      ascent: measured.actualBoundingBoxAscent,
      descent: measured.actualBoundingBoxDescent
    });
  };

  const rotatePoint = (x, y, angle) => ({
    x: x * Math.cos(angle) - y * Math.sin(angle),
    y: x * Math.sin(angle) + y * Math.cos(angle)
  });

  const resolveFont = (catalog, layer) => {
    const fonts = catalog?.fonts || catalog;
    const font = fonts && Object.hasOwn(fonts, layer.fontId) ? fonts[layer.fontId] : null;
    if (!font || font.revision !== layer.fontRevision || typeof font.family !== 'string'
      || !Number.isFinite(Number(font.weight))) {
      throw new Error('This saved font is unavailable.');
    }
    return font;
  };

  const layoutLayer = (context, input, width, height, fontCatalog) => {
    const layer = model.validateLayers([input])[0];
    if (!context || typeof context.measureText !== 'function') throw new Error('Your browser cannot prepare lettering.');
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      throw new Error('This product artwork size is unavailable.');
    }
    const font = resolveFont(fontCatalog, layer);
    const size = layer.size * Math.min(width, height);
    const baselineOffset = size * 0.34;
    const runs = [];

    context.save();
    try {
      configure(context, font, size);
      if (!layer.curve) {
        const lines = model.normaliseLineBreaks(layer.text).split('\n').map((text) => ({ text, metrics: glyphMetrics(context, text) }));
        const maximumAdvance = Math.max(0, ...lines.map((line) => line.metrics.width));
        lines.forEach(({ text, metrics }, index) => {
          const dx = layer.align === 'left'
            ? -maximumAdvance / 2
            : (layer.align === 'right' ? maximumAdvance / 2 - metrics.width : -metrics.width / 2);
          runs.push(Object.freeze({
            text,
            x: 0,
            y: (index - (lines.length - 1) / 2) * size * 1.22,
            angle: 0,
            dx,
            dy: baselineOffset,
            metrics
          }));
        });
      } else {
        const glyphs = model.graphemes(layer.text).map((text) => ({ text, metrics: glyphMetrics(context, text) }));
        const length = glyphs.reduce((total, glyph) => total + glyph.metrics.width, 0);
        const curvature = length ? layer.curve * Math.PI / 180 / length : 0;
        let distance = -length / 2;
        for (const glyph of glyphs) {
          const point = arcPoint(distance + glyph.metrics.width / 2, curvature);
          runs.push(Object.freeze({
            ...point,
            text: glyph.text,
            dx: -glyph.metrics.width / 2,
            dy: baselineOffset,
            metrics: glyph.metrics
          }));
          distance += glyph.metrics.width;
        }
      }
    } finally {
      context.restore();
    }

    const rotation = layer.rotation * Math.PI / 180;
    const anchor = Object.freeze({ x: layer.x * width, y: layer.y * height });
    let bounds = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
    for (const run of runs) {
      const metrics = run.metrics;
      const corners = [
        [run.dx - metrics.left, run.dy - metrics.ascent],
        [run.dx + metrics.right, run.dy - metrics.ascent],
        [run.dx + metrics.right, run.dy + metrics.descent],
        [run.dx - metrics.left, run.dy + metrics.descent]
      ];
      for (const [x, y] of corners) {
        const glyphPoint = rotatePoint(x, y, run.angle);
        const layerPoint = rotatePoint(run.x + glyphPoint.x, run.y + glyphPoint.y, rotation);
        const worldX = layerPoint.x + anchor.x;
        const worldY = layerPoint.y + anchor.y;
        bounds.left = Math.min(bounds.left, worldX);
        bounds.top = Math.min(bounds.top, worldY);
        bounds.right = Math.max(bounds.right, worldX);
        bounds.bottom = Math.max(bounds.bottom, worldY);
      }
    }
    if (!runs.length) bounds = { left: anchor.x, top: anchor.y, right: anchor.x, bottom: anchor.y };
    return Object.freeze({
      layer: Object.freeze(layer),
      font,
      size,
      runs: Object.freeze(runs),
      rotation,
      anchor,
      bounds: Object.freeze(bounds)
    });
  };

  const layoutLayers = (context, layers, width, height, fontCatalog) => Object.freeze(
    model.validateLayers(layers).map((layer) => layoutLayer(context, layer, width, height, fontCatalog))
  );

  const drawLayout = (context, layout) => {
    context.save();
    try {
      configure(context, layout.font, layout.size);
      context.fillStyle = layout.layer.colour;
      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
      context.translate(layout.anchor.x, layout.anchor.y);
      context.rotate(layout.rotation);
      for (const run of layout.runs) {
        context.save();
        try {
          context.translate(run.x, run.y);
          context.rotate(run.angle);
          context.fillText(run.text, run.dx, run.dy);
        } finally {
          context.restore();
        }
      }
    } finally {
      context.restore();
    }
  };

  const drawLayouts = (context, layouts) => layouts.forEach((layout) => drawLayout(context, layout));

  const isInside = (bounds, region, tolerance = 0) => {
    const threshold = Number(tolerance);
    if (!Number.isFinite(threshold) || threshold < 0) throw new Error('Invalid bounds tolerance.');
    return bounds.left >= region.left - threshold
      && bounds.right <= region.right + threshold
      && bounds.top >= region.top - threshold
      && bounds.bottom <= region.bottom + threshold;
  };

  const hit = (bounds, x, y, padding = 0) => x >= bounds.left - padding && x <= bounds.right + padding
    && y >= bounds.top - padding && y <= bounds.bottom + padding;

  return Object.freeze({ arcPoint, glyphMetrics, layoutLayer, layoutLayers, drawLayout, drawLayouts, isInside, hit });
}));
